import React from "react";
import { NavLink } from "react-router-dom";
import {
  Home,
  Megaphone,
  Star,
  LayoutDashboard,
  Banknote,
  Users,
  Trophy,
  Shield,
  ShieldCheck,
  BarChart3,
  ChevronsLeft,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/contexts/AuthContext";

const DISCORD_INVITE = "https://discord.gg/clippingbase";

function SidebarLink({ to, icon: Icon, label, collapsed, testId, matchPrefix, end = true }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      title={label}
      aria-label={label}
      className={({ isActive: routerActive }) => {
        const active = matchPrefix
          ? window.location.pathname === matchPrefix ||
            window.location.pathname.startsWith(matchPrefix + "/")
          : routerActive;
        return [
          "group relative flex items-center rounded-[10px] transition-colors duration-150",
          collapsed ? "h-10 w-10 justify-center mx-auto" : "h-9 w-full gap-3 px-3",
          active
            ? "bg-white/[0.06] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
            : "text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-200",
        ].join(" ");
      }}
    >
      {({ isActive: routerActive }) => {
        const active = matchPrefix
          ? window.location.pathname === matchPrefix ||
            window.location.pathname.startsWith(matchPrefix + "/")
          : routerActive;
        return (
          <>
            <Icon
              size={collapsed ? 18 : 17}
              strokeWidth={active ? 2.1 : 1.8}
              className="shrink-0"
            />
            {!collapsed && (
              <span className="truncate text-[13.5px] font-medium tracking-tight">
                {label}
              </span>
            )}
          </>
        );
      }}
    </NavLink>
  );
}

function Group({ children, collapsed, className = "" }) {
  return (
    <div
      className={[
        "space-y-1",
        collapsed ? "px-1.5" : "px-2",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function Divider({ collapsed }) {
  return (
    <div className={collapsed ? "mx-3 my-2" : "mx-3 my-2.5"}>
      <div className="h-px w-full bg-white/[0.06]" />
    </div>
  );
}

export function DesktopSidebar({ collapsed, onToggleCollapse }) {
  const { hasAccess, me } = useAuth();

  const adminPages = ["dashboard", "submissions", "socials", "users", "wallet", "payouts", "analytics", "roles", "contact", "announcements"];
  const adminPerms = ["view_dashboard", "view_analytics", "review_submissions", "manage_payouts", "manage_roles", "view_creator_insights"];
  const modPages = ["mod_dashboard", "mod_submissions", "mod_socials", "mod_users", "mod_wallet", "mod_templates", "mod_bulk", "mod_announcements"];
  const modPerms = ["review_submissions", "manage_payouts"];

  const canAdminSection = hasAccess(adminPages, adminPerms);
  const showModSection = hasAccess(modPages, modPerms);

  const roles = me?.roles || (me?.role ? [me.role] : []);
  const hasBrandRole = roles.includes("brand") || roles.includes("admin");
  const canViewInsights = hasBrandRole || hasAccess(["campaign_insights"], ["view_campaign_insights"]);
  const hasStaff = canAdminSection || showModSection || canViewInsights;

  return (
    <aside
      data-testid="desktop-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={[
        "fixed inset-y-0 left-0 z-40 flex flex-col",
        "border-r border-white/[0.06] bg-[#0f0f0f]",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-[64px]" : "w-[220px]",
      ].join(" ")}
    >
      {/* Header: Logo + collapse toggle */}
      <div
        data-testid="desktop-sidebar-header"
        className={[
          "flex items-center border-b border-white/[0.06]",
          collapsed ? "h-[60px] justify-center px-2" : "h-[60px] justify-between px-4",
        ].join(" ")}
      >
        <NavLink to="/home" className="flex items-center overflow-hidden">
          {collapsed ? (
            <BrandLogo plain iconSize={30} showText={false} testId="desktop-sidebar-logo" />
          ) : (
            <BrandLogo
              plain
              iconSize={30}
              testId="desktop-sidebar-logo"
              textClassName="text-[15px] font-extrabold tracking-tight text-white"
            />
          )}
        </NavLink>
        {!collapsed && (
          <button
            type="button"
            data-testid="desktop-sidebar-collapse"
            onClick={onToggleCollapse}
            title="Collapse sidebar"
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200"
          >
            <ChevronsLeft size={16} />
          </button>
        )}
      </div>

      {/* Nav area (scrollable) */}
      <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto py-3 no-scrollbar">
        {/* Group A: Discover */}
        <Group collapsed={collapsed}>
          <SidebarLink to="/home" icon={Home} label="Home" testId="sidebar-home" collapsed={collapsed} />
          <SidebarLink to="/campaigns" icon={Megaphone} label="Campaigns" testId="sidebar-campaigns" collapsed={collapsed} matchPrefix="/campaigns" end={false} />
          <SidebarLink to="/my-campaigns" icon={Star} label="My Campaigns" testId="sidebar-my-campaigns" collapsed={collapsed} />
        </Group>

        <Divider collapsed={collapsed} />

        {/* Group B: Creator utilities */}
        <Group collapsed={collapsed}>
          <SidebarLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="sidebar-dashboard" collapsed={collapsed} />
          <SidebarLink to="/earnings" icon={Banknote} label="Earnings" testId="sidebar-earnings" collapsed={collapsed} matchPrefix="/earnings" end={false} />
          <SidebarLink to="/socials" icon={Users} label="Accounts" testId="sidebar-accounts" collapsed={collapsed} />
          <SidebarLink to="/leaderboard" icon={Trophy} label="Leaderboard" testId="sidebar-leaderboard" collapsed={collapsed} />
        </Group>

        {hasStaff && (
          <>
            <Divider collapsed={collapsed} />
            <Group collapsed={collapsed}>
              {canAdminSection && (
                <SidebarLink to="/admin" icon={Shield} label="Admin" testId="sidebar-admin" collapsed={collapsed} matchPrefix="/admin" end={false} />
              )}
              {showModSection && (
                <SidebarLink to="/mod" icon={ShieldCheck} label="Moderator" testId="sidebar-mod" collapsed={collapsed} matchPrefix="/mod" end={false} />
              )}
              {canViewInsights && (
                <SidebarLink to="/insights" icon={BarChart3} label="Insights" testId="sidebar-insights" collapsed={collapsed} />
              )}
            </Group>
          </>
        )}
      </nav>

      {/* Community footer */}
      <div className="border-t border-white/[0.06] p-2">
        <a
          href={DISCORD_INVITE}
          target="_blank"
          rel="noreferrer"
          data-testid="sidebar-community-link"
          title="Community"
          className={[
            "flex items-center rounded-[10px] text-zinc-400 transition-colors hover:bg-white/[0.04] hover:text-white",
            collapsed ? "h-10 w-10 mx-auto justify-center" : "h-10 gap-3 px-3",
          ].join(" ")}
        >
          <svg viewBox="0 0 24 24" width={collapsed ? 20 : 18} height={collapsed ? 20 : 18} fill="currentColor" className="shrink-0">
            <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 13.79 13.79 0 0 0-.608 1.249 18.27 18.27 0 0 0-5.487 0 12.65 12.65 0 0 0-.617-1.249.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          {!collapsed && (
            <span className="text-[13.5px] font-medium">Community</span>
          )}
        </a>
      </div>
    </aside>
  );
}
